/**
 * Client side of the multi-device "share this game" feature — see
 * relay/gameShare.ts for the full design rationale (why two tokens, why the
 * relay never validates round payloads, why this is a deliberate narrow
 * amendment to the relay's "no game state" boundary).
 *
 * Two tokens, two very different clients of this module:
 *  - The main device holds both `shareToken` and `ownerToken` (persisted
 *    locally) and can call every function here.
 *  - A guest device holds only `shareToken` (from the link it was sent) and
 *    must only ever call the guest-safe functions: fetchShareState,
 *    submitGuestRound. Calling an owner-only function with a share token
 *    fails server-side (403) — that check is what makes "guest can't edit
 *    the last round or end the game" real, not just a hidden button. This
 *    module doesn't re-enforce it client-side; the UI simply shouldn't wire
 *    those actions into the guest screen at all.
 */

import type { RoundRecord } from '../game/types';
import { READ_TIMEOUT_MS, RequestTimeoutError, SEND_TIMEOUT_MS, fetchWithTimeout } from './timedFetch';

const RELAY_BASE = import.meta.env?.VITE_VISION_RELAY_URL ?? '/api';

const STORAGE_KEY = 'mahjong-scorer:gameShare';

export interface SharedPlayer {
  id: string;
  name: string;
}

/** Mirrors `src/ui/branding.ts`'s `Branding` — see `relay/gameShare.ts`'s `SharedBranding`. */
export interface SharedBranding {
  name: string;
  logoDataUrl?: string;
  winnerMessage?: string;
}

export interface SharedGameState {
  gameId: string;
  tableId: string;
  players: SharedPlayer[];
  rulesetId: string;
  settings: unknown;
  branding?: SharedBranding;
  createdAt: number;
  /** Mirrors the real `Game.startedAt` — when the game itself started, set once at share creation. */
  gameStartedAt?: number;
  /** Mirrors the real `Game.endedAt` — distinct from the relay's own `endedAt` bookkeeping (see `relay/gameShare.ts`'s `SharedGameState.gameEndedAt`). Cleared on "go back to this game". */
  gameEndedAt?: number;
  ended: boolean;
  invalidated: boolean;
  totals: Record<string, number>;
  roundCount: number;
  lastRoundSummary?: string;
  /**
   * The full round-by-round history — complete `RoundRecord`s (fan,
   * patterns, tiles, photos, the works), not a trimmed-down summary, so a
   * guest device can render the exact same round detail and end-of-game
   * ranking the main device shows. Republished (whole array) alongside
   * `totals`/`roundCount` whenever either changes.
   */
  roundHistory?: RoundRecord[];
  lastUpdatedAt: number;
  /** Set when the main device tried to apply this device's last round submission and it failed — see `relay/gameShare.ts`'s `PublishUpdate.lastRejection`. */
  lastRejection?: { text: string; at: number };
  /** The host is still deciding who sits where — mirrors `Game.seatingPending`; the guest can't record a round until it clears. See `relay/gameShare.ts`. */
  seatingPending?: boolean;
  /** When the relay saw the game end — a guest's link stops working an hour after this (see `photoDelivery.ts`'s `guestLinkDeadline`). */
  endedAt?: number;
  /** Rounds whose photos the host is holding back because of a slow connection; the guest fetches them separately. See `photoDelivery.ts`. */
  photoRounds?: number[];
}

export interface PendingRoundSubmission {
  submissionId: string;
  submittedAt: number;
  kind: 'win' | 'draw';
  payload: unknown;
}

export interface StoredShare {
  gameId: string;
  shareToken: string;
  ownerToken: string;
}

/** Persisted on the main device only — never put the ownerToken in the shareable link. */
export function saveStoredShare(share: StoredShare): void {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(share));
}

export function getStoredShare(): StoredShare | undefined {
  const raw = localStorage.getItem(STORAGE_KEY);
  if (!raw) return undefined;
  try {
    const parsed = JSON.parse(raw) as StoredShare;
    if (typeof parsed.gameId !== 'string' || typeof parsed.shareToken !== 'string' || typeof parsed.ownerToken !== 'string') {
      return undefined;
    }
    return parsed;
  } catch {
    return undefined;
  }
}

export function clearStoredShare(): void {
  localStorage.removeItem(STORAGE_KEY);
}

/** Builds the guest-facing URL to hand out for a given share token. */
export function buildGuestLink(shareToken: string): string {
  const url = new URL(window.location.href);
  url.hash = `guest=${shareToken}`;
  return url.toString();
}

const PENDING_SUBMISSION_KEY_PREFIX = 'mahjong-scorer:guestPendingSubmission:';

/**
 * A real incident (2026-09-12): a guest hit a validation error, and again,
 * before a submission finally went through — and every one of those retries
 * was counted as a separate real round, because `guestPlay.ts`'s `wireUp`
 * used to mint a fresh `crypto.randomUUID()` on every render, and a LOT of
 * things re-render this screen that all still represent the SAME logical
 * attempt from the guest's point of view: a validation failure that DOES
 * reach the relay (as opposed to one caught by the client-side pre-checks,
 * which never re-render at all), a Scan Photo round trip, tapping Refresh,
 * or just closing and reopening the tab because they weren't sure the first
 * tap worked. `submitRound`'s relay-side dedup (see relay/gameShare.ts) only
 * ever had a chance to work if the SAME id survived all of that — it didn't.
 *
 * Persisting the id here instead of in a render-local variable makes it
 * survive every one of those cases. It's retired (see
 * `clearPendingSubmissionId`) only once a submission attempt actually
 * reaches the relay successfully — at that point the relay has durably
 * recorded something under it (either freshly pending, or already
 * consumed), so reusing it for whatever the guest enters next would be
 * wrong: `submitRound` would just hand back that old recorded content
 * instead of the new round.
 */
export function getOrCreatePendingSubmissionId(gameId: string): string {
  const key = PENDING_SUBMISSION_KEY_PREFIX + gameId;
  const existing = localStorage.getItem(key);
  if (existing) return existing;
  const id = crypto.randomUUID();
  localStorage.setItem(key, id);
  return id;
}

/** Call once a submission attempt to this game succeeds — see `getOrCreatePendingSubmissionId`'s doc comment for why. */
export function clearPendingSubmissionId(gameId: string): void {
  localStorage.removeItem(PENDING_SUBMISSION_KEY_PREFIX + gameId);
}

/**
 * `network` is set when the request never got an answer (offline, poor signal, the relay
 * unreachable) and `status` when the relay answered with an error. Callers use them to tell
 * "try again, nothing is wrong with what you sent" apart from "this was refused".
 */
export type ShareResult<T> =
  | { ok: true; data: T }
  | { ok: false; error: string; network?: boolean; status?: number; /** The request was accepted but never answered within its deadline (a slow connection), as opposed to failing at once. */ timedOut?: boolean };

/** What a person sees when a request could not get through — worded so nobody worries that data was lost. */
export const NETWORK_ERROR = "Couldn't reach the server. Nothing was lost — check the connection and try again.";

async function parseError(res: Response): Promise<string> {
  const body = (await res.json().catch(() => ({}))) as { error?: string };
  return body.error ?? `Request failed (${res.status})`;
}

/**
 * `fetch` that reports a network failure as `undefined` instead of throwing. Every call in this
 * module used to let a dropped connection throw out of an `async` handler nobody awaited, so on a
 * poor connection a button simply did nothing and gave no hint why. A request that is accepted but
 * never answered counts as a network failure too (`fetchWithTimeout`), so nothing waits forever.
 */
async function safeFetch(url: string, init?: RequestInit, timeoutMs?: number): Promise<Response | NetworkFailed> {
  try {
    return await fetchWithTimeout(url, init, timeoutMs ?? (init?.body ? SEND_TIMEOUT_MS : READ_TIMEOUT_MS));
  } catch (err) {
    return new NetworkFailed(err instanceof RequestTimeoutError);
  }
}

/** What `safeFetch` hands back instead of a Response when the request never got an answer. */
class NetworkFailed {
  constructor(readonly timedOut: boolean) {}
}

function networkFailure(failed?: NetworkFailed): { ok: false; error: string; network: true; timedOut?: true } {
  return { ok: false, error: NETWORK_ERROR, network: true, ...(failed?.timedOut ? { timedOut: true as const } : {}) };
}

async function failure(res: Response): Promise<{ ok: false; error: string; status: number }> {
  return { ok: false, error: await parseError(res), status: res.status };
}

/** Worth trying again later: the network dropped, or the relay itself had a problem (5xx). A refusal (4xx) is not. */
export function isRetryable(result: { ok: boolean; network?: boolean; status?: number }): boolean {
  return !result.ok && (result.network === true || (result.status ?? 0) >= 500);
}

/** Main device only — requires an active activation session (see relay's rationale). */
export async function createShare(params: {
  sessionToken: string;
  gameId: string;
  tableId: string;
  players: SharedPlayer[];
  /** Mirrors `Game.seatingPending` — pass true while the seating is still undecided. */
  seatingPending?: boolean;
  rulesetId: string;
  settings: unknown;
  branding?: SharedBranding;
  /** Mirrors `Game.startedAt` — when the game itself started, not when it was shared. */
  gameStartedAt?: number;
  /** Current totals/round count/history — pass these when sharing a game that already has rounds recorded, so the guest's mirror doesn't start at zero. */
  totals?: Record<string, number>;
  roundCount?: number;
  roundHistory?: RoundRecord[];
}): Promise<ShareResult<StoredShare>> {
  const res = await safeFetch(`${RELAY_BASE}/share/create`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${params.sessionToken}` },
    body: JSON.stringify({
      gameId: params.gameId,
      tableId: params.tableId,
      players: params.players,
      seatingPending: params.seatingPending,
      rulesetId: params.rulesetId,
      settings: params.settings,
      branding: params.branding,
      gameStartedAt: params.gameStartedAt,
      totals: params.totals,
      roundCount: params.roundCount,
      roundHistory: params.roundHistory,
    }),
  });
  if (res instanceof NetworkFailed) return networkFailure(res);
  if (!res.ok) return failure(res);
  const body = (await res.json()) as { shareToken: string; ownerToken: string };
  return { ok: true, data: { gameId: params.gameId, shareToken: body.shareToken, ownerToken: body.ownerToken } };
}

/** Guest or owner. The public mirror any shared-link device polls to refresh its view. */
export async function fetchShareState(token: string): Promise<ShareResult<SharedGameState>> {
  const res = await safeFetch(`${RELAY_BASE}/share/state`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (res instanceof NetworkFailed) return networkFailure(res);
  if (!res.ok) return failure(res);
  const body = (await res.json()) as { state: SharedGameState };
  return { ok: true, data: body.state };
}

/**
 * Result of a background poll. `gone` separates "this link/token is no longer valid" (stop
 * polling, tell the person) from "the network hiccuped" (keep trying, more slowly) — a plain
 * error string can't say which.
 */
export type PollResult<T> = { ok: true; data: T } | { ok: false; error: string; gone: boolean };

async function pollJson<T>(url: string, token: string): Promise<PollResult<T>> {
  try {
    const res = await fetchWithTimeout(url, { headers: { Authorization: `Bearer ${token}` } }, READ_TIMEOUT_MS);
    if (!res.ok) {
      return { ok: false, error: await parseError(res), gone: res.status === 401 || res.status === 403 || res.status === 404 };
    }
    return { ok: true, data: (await res.json()) as T };
  } catch {
    return { ok: false, error: 'Could not reach the server.', gone: false };
  }
}

/**
 * The guest's background check. Says which version it already has, so an unchanged game
 * costs the relay two small KV reads and a few bytes back instead of re-sending every round
 * (with its photos). Returns `unchanged` or the full new state.
 */
export async function fetchShareStateIfChanged(
  token: string,
  sinceLastUpdatedAt: number,
): Promise<PollResult<{ unchanged: true } | { unchanged: false; state: SharedGameState }>> {
  const result = await pollJson<{ unchanged?: boolean; state?: SharedGameState }>(
    `${RELAY_BASE}/share/state?since=${encodeURIComponent(String(sinceLastUpdatedAt))}`,
    token,
  );
  if (!result.ok) return result;
  if (result.data.unchanged) return { ok: true, data: { unchanged: true } };
  if (!result.data.state) return { ok: false, error: 'Unexpected reply from the server.', gone: false };
  return { ok: true, data: { unchanged: false, state: result.data.state } };
}

/** Owner only. A stamp that changes whenever a guest submits a new round (null if none ever has) — the host's cheapest "is anything waiting?" check. */
export async function fetchShareSignal(ownerToken: string): Promise<PollResult<string | null>> {
  const result = await pollJson<{ signal: string | null }>(`${RELAY_BASE}/share/signal`, ownerToken);
  return result.ok ? { ok: true, data: result.data.signal ?? null } : result;
}

/** Owner only. How many guest rounds are waiting, without downloading them (they can carry photos). */
export async function fetchPendingCount(ownerToken: string): Promise<PollResult<number>> {
  const result = await pollJson<{ count: number }>(`${RELAY_BASE}/share/pending?count=1`, ownerToken);
  return result.ok ? { ok: true, data: Number(result.data.count) || 0 } : result;
}

/**
 * Guest or owner. Appends a round proposal for the main device to review and
 * apply locally. `clientSubmissionId`, when passed, makes a retried call
 * with the SAME id a no-op on the relay rather than a second, separate round
 * — see `relay/gameShare.ts`'s `submitRound` for the full rationale.
 */
export async function submitRound(
  token: string,
  kind: 'win' | 'draw',
  payload: unknown,
  clientSubmissionId?: string,
): Promise<ShareResult<{ submissionId: string }>> {
  const res = await safeFetch(`${RELAY_BASE}/share/round`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
    body: JSON.stringify({ kind, payload, clientSubmissionId }),
  });
  if (res instanceof NetworkFailed) return networkFailure(res);
  if (!res.ok) return failure(res);
  const body = (await res.json()) as { submissionId: string };
  return { ok: true, data: body };
}

/** Owner only — 403s for a guest token. */
export async function fetchPending(ownerToken: string): Promise<ShareResult<PendingRoundSubmission[]>> {
  const res = await safeFetch(`${RELAY_BASE}/share/pending`, {
    headers: { Authorization: `Bearer ${ownerToken}` },
  });
  if (res instanceof NetworkFailed) return networkFailure(res);
  if (!res.ok) return failure(res);
  const body = (await res.json()) as { pending: PendingRoundSubmission[] };
  return { ok: true, data: body.pending };
}

/** Owner only — call after applying the given submissions locally via recordWin/recordDraw. */
export async function consumePending(ownerToken: string, submissionIds: string[]): Promise<ShareResult<true>> {
  const res = await safeFetch(`${RELAY_BASE}/share/consume`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${ownerToken}` },
    body: JSON.stringify({ submissionIds }),
  });
  if (res instanceof NetworkFailed) return networkFailure(res);
  if (!res.ok) return failure(res);
  return { ok: true, data: true };
}

/** Owner only — kills the current guest link and mints a fresh one; see relay/gameShare.ts's `regenerateShareToken` for the rationale. */
export async function regenerateShareLink(
  ownerToken: string,
  oldShareToken: string,
): Promise<ShareResult<{ shareToken: string }>> {
  const res = await safeFetch(`${RELAY_BASE}/share/regenerate-link`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${ownerToken}` },
    body: JSON.stringify({ oldShareToken }),
  });
  if (res instanceof NetworkFailed) return networkFailure(res);
  if (!res.ok) return failure(res);
  const body = (await res.json()) as { shareToken: string };
  return { ok: true, data: body };
}

export interface PublishUpdate {
  /** The same four players in seating order — sent once when the host sets the seating (the relay accepts only a reordering). */
  players?: SharedPlayer[];
  seatingPending?: boolean;
  totals?: Record<string, number>;
  roundCount?: number;
  lastRoundSummary?: string;
  roundHistory?: RoundRecord[];
  ended?: boolean;
  /** Mirrors `Game.endedAt` — see `SharedGameState.gameEndedAt`. Pass explicit `undefined` alongside `ended: false` to clear it. */
  gameEndedAt?: number;
  invalidated?: boolean;
  /** `null` clears the notice on guest screens; leave it out to leave it as it is. */
  lastRejection?: { text: string; at: number } | null;
  /** See `SharedGameState.photoRounds`. */
  photoRounds?: number[];
}

/** Owner only — the only way the public mirror's totals/roundCount/ended ever change. */
export async function publishState(ownerToken: string, update: PublishUpdate): Promise<ShareResult<SharedGameState>> {
  const res = await safeFetch(`${RELAY_BASE}/share/publish`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${ownerToken}` },
    body: JSON.stringify(update),
  });
  if (res instanceof NetworkFailed) return networkFailure(res);
  if (!res.ok) return failure(res);
  const body = (await res.json()) as { state: SharedGameState };
  return { ok: true, data: body.state };
}

// ---------------------------------------------------------------------------
// Photos that travel separately from the game — see `photoDelivery.ts` for when and why.
// ---------------------------------------------------------------------------

/** A photo request moves a few hundred KB either way, so it gets the longer deadline whichever direction it goes. */
const PHOTO_TIMEOUT_MS = SEND_TIMEOUT_MS;

/** Guest or owner: sends the photo(s) for a round that was submitted without them. */
export async function uploadSubmissionPhotos(token: string, submissionId: string, photos: string[]): Promise<ShareResult<true>> {
  const res = await safeFetch(
    `${RELAY_BASE}/share/submission-photos`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify({ submissionId, photos }),
    },
    PHOTO_TIMEOUT_MS,
  );
  if (res instanceof NetworkFailed) return networkFailure(res);
  if (!res.ok) return failure(res);
  return { ok: true, data: true };
}

/** Owner only: the photos guests have sent for the given submissions. Ids with no photo yet are absent from the result. */
export async function fetchSubmissionPhotos(ownerToken: string, ids: string[]): Promise<ShareResult<Record<string, string[]>>> {
  const res = await safeFetch(
    `${RELAY_BASE}/share/submission-photos?ids=${encodeURIComponent(ids.join(','))}`,
    { headers: { Authorization: `Bearer ${ownerToken}` } },
    PHOTO_TIMEOUT_MS,
  );
  if (res instanceof NetworkFailed) return networkFailure(res);
  if (!res.ok) return failure(res);
  const body = (await res.json()) as { photos?: Record<string, string[]> };
  return { ok: true, data: body.photos ?? {} };
}

/** Owner only: uploads one round's photos for the guests' copy. */
export async function uploadRoundPhotos(
  ownerToken: string,
  roundIndex: number,
  recordedAt: number,
  photos: string[],
): Promise<ShareResult<true>> {
  const res = await safeFetch(
    `${RELAY_BASE}/share/round-photos`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${ownerToken}` },
      body: JSON.stringify({ roundIndex, recordedAt, photos }),
    },
    PHOTO_TIMEOUT_MS,
  );
  if (res instanceof NetworkFailed) return networkFailure(res);
  if (!res.ok) return failure(res);
  return { ok: true, data: true };
}

/** Guest or owner: the host's photos for the given rounds, each stamped with the round's `recordedAt`. Rounds not uploaded yet are absent. */
export async function fetchRoundPhotos(
  token: string,
  roundIndices: number[],
): Promise<ShareResult<Record<string, { recordedAt: number; photos: string[] }>>> {
  const res = await safeFetch(
    `${RELAY_BASE}/share/round-photos?rounds=${encodeURIComponent(roundIndices.join(','))}`,
    { headers: { Authorization: `Bearer ${token}` } },
    PHOTO_TIMEOUT_MS,
  );
  if (res instanceof NetworkFailed) return networkFailure(res);
  if (!res.ok) return failure(res);
  const body = (await res.json()) as { rounds?: Record<string, { recordedAt: number; photos: string[] }> };
  return { ok: true, data: body.rounds ?? {} };
}
